class RoRelatedProducts extends HTMLElement {
  static VISITOR_HISTORY_TYPE = 'recommendations/visitor/history';

  static LOADED_CLASS = 'ro-related-products--loaded';

  constructor() {
    super();
    this.onConsentUpdated = this.onConsentUpdated.bind(this);
  }

  connectedCallback() {
    if (!this.dataset.ak) {
      console.error('Related products: API key is missing');
      return;
    }

    if (!this.dataset.type) {
      console.warn('Related products: no type specified');
      return;
    }

    this.fetchWhenConsentApproved();
  }

  disconnectedCallback() {
    this.clearConsentWatchers();
  }

  /**
   * Stops listening for consent updates.
   * @returns {void}
   */
  clearConsentWatchers() {
    window.removeEventListener('ro:cookie-consent-updated', this.onConsentUpdated);
    window.clearInterval(this.consentPollInterval);
  }

  /**
   * Loads Clerk recommendations after analytics consent is granted.
   * @returns {void}
   */
  fetchWhenConsentApproved() {
    if (this.relatedProductsLoaded) {
      return;
    }

    const loadRecommendations = () => {
      if (this.relatedProductsLoaded) {
        return;
      }

      this.relatedProductsLoaded = true;
      this.clearConsentWatchers();
      this.fetch();
    };

    if (!window.RoRelatedProductsConfig?.consentRequired) {
      loadRecommendations();
      return;
    }

    if (window.RoClerkConsentApproved === true) {
      loadRecommendations();
      return;
    }

    if (window.RoClerkConsentApproved === false) {
      return;
    }

    window.addEventListener('ro:cookie-consent-updated', this.onConsentUpdated);

    this.consentPollInterval = window.setInterval(() => {
      if (window.RoClerkConsentApproved === true) {
        loadRecommendations();
      }
    }, 300);
  }

  /**
   * Handles cookie consent updates from Iubenda.
   * @param {CustomEvent} event
   * @returns {void}
   */
  onConsentUpdated(event) {
    if (event?.detail?.clerkConsentApproved !== true || this.relatedProductsLoaded) {
      return;
    }

    this.relatedProductsLoaded = true;
    this.clearConsentWatchers();
    this.fetch();
  }

  /**
   * Builds the Clerk API query object for the current recommendation type.
   * @returns {object}
   */
  buildQueryObj() {
    const queryObj = {
      key: this.dataset.ak,
      limit: 4,
      attributes: ['handle'],
    };

    if (this.dataset.productId) {
      queryObj.exclude = [this.dataset.productId];
    }

    if (
      this.dataset.type !== RoRelatedProducts.VISITOR_HISTORY_TYPE &&
      this.dataset.type !== 'recommendations/popular' &&
      this.dataset.productId
    ) {
      queryObj.products = [this.dataset.productId];
    }

    if (this.dataset.productGender) {
      queryObj.filter = `gender = '${RoRelatedProducts.escapeFilterValue(this.dataset.productGender)}'`;
    }

    return queryObj;
  }

  fetch() {
    Clerk(
      'call',
      this.dataset.type,
      this.buildQueryObj(),
      (response) => {
        const handles = [
          ...new Set(
            (response.product_data ?? [])
              .map((product) => product.handle)
              .filter(Boolean)
          ),
        ];

        if (handles.length) {
          this.loadSection(handles);
        }
      },
      (error) => {
        console.error('Related products: Clerk.io error:', error);
      }
    );
  }

  /**
   * Loads the Shopify section that renders the product cards.
   * @param {string[]} handles
   * @returns {void}
   */
  loadSection(handles) {
    const url =
      `${window.location.pathname}?sections=ro-related-products-content&handles=` +
      encodeURIComponent(handles.join(','));

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const innerHtml = this.getSectionInnerHTML(data['ro-related-products-content']);

        if (!innerHtml?.trim()) {
          return;
        }

        this.innerHTML = innerHtml;
        this.markLoadedIfHasProducts();
      })
      .catch((error) => {
        console.error('Error loading the section:', error);
      });
  }

  /**
   * Marks the component as loaded when product cards are present.
   * @returns {void}
   */
  markLoadedIfHasProducts() {
    const hasProducts =
      this.querySelector('[data-clerk-product-id]') ||
      (this.querySelector('.tw-grid')?.childElementCount ?? 0) > 0;

    if (hasProducts) {
      this.classList.add(RoRelatedProducts.LOADED_CLASS);
    }
  }

  /**
   * Extracts the inner HTML of a rendered Shopify section.
   * @param {string} html
   * @param {string} [selector='.shopify-section']
   * @returns {string}
   */
  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);

    return section?.innerHTML ?? '';
  }

  /**
   * Escapes single quotes in Clerk filter values.
   * @param {string} value
   * @returns {string}
   */
  static escapeFilterValue(value) {
    return String(value).replace(/'/g, "\\'");
  }
}

customElements.define('ro-related-products', RoRelatedProducts);

class RoComplementaryProducts extends RoRelatedProducts {
  static CLERK_API = 'recommendations/complementary';
  static CLERK_FALLBACK_API = 'recommendations/trending';
  static CLERK_LIMIT = 50;

  static SLOTS = {
    tops: [
      'T-SHIRTS/SHIRTS##T-SHIRTS',
      'T-SHIRTS / SHIRTS##TANK TOPS',
      'T-SHIRTS / SHIRTS##SHIRTS',
      'KNITWEAR##TOPS',
      'TOPS##TOPS',
      'OUTERWEAR##WOVEN JACKETS',
      'OUTERWEAR##LEATHER JACKETS',
      'OUTERWEAR##COATS',
      'OUTERWEAR##DUVETS',
      'OUTERWEAR##DENIM JACKETS',
      'DRESS##DRESSES',
      'SWEATSHIRTS / HOODIES##HOODIES',
      'SWEATSHIRTS / HOODIES##SWEATSHIRTS',
    ],
    bottoms: [
      'BOTTOMS##PANTS',
      'BOTTOMS##SKIRTS',
      'BOTTOMS##SHORTS',
      'SWIMWEAR / UNDERWEAR##SOCKS',
    ],
    accessories: [
      'ACESSORIES##EYEWEAR',
      'ACCESSORIES##SCARVES',
      'BAGS##SHOULDER BAGS',
      'HOME##OBJECTS',
      'JEWELERY##RINGS',
    ],
    shoes: [
      'SHOES##SNEAKERS',
      'SHOES##HEELS',
      'SHOES##BOOTS',
    ],
  };

  connectedCallback() {
    if (!this.dataset.ak) {
      console.error('Styled with products: API key is missing');
      return;
    }

    this.fetch();
  }

  async fetch() {
    const complementaryProducts = await this.fetchComplementaryProducts();
    const trendingProducts = await this.fetchTrendingProducts();
    const handles = this.getSlotHandles(complementaryProducts, trendingProducts);

    if (handles.length) {
      this.loadSection(handles);
    }
  }

  fetchComplementaryProducts() {
    const queryObj = this.getBaseQuery();

    if (this.dataset.productId) {
      queryObj.products = [this.dataset.productId];
    }

    return this.fetchClerkProducts(RoComplementaryProducts.CLERK_API, queryObj, 'Styled with products');
  }

  fetchTrendingProducts() {
    return this.fetchClerkProducts(
      RoComplementaryProducts.CLERK_FALLBACK_API,
      this.getBaseQuery(),
      'Styled with products fallback'
    );
  }

  fetchClerkProducts(api, queryObj, logPrefix) {
    return new Promise((resolve) => {
      Clerk(
        'call',
        api,
        queryObj,
        (response) => {
          resolve(response.product_data ?? []);
        },
        (response) => {
          console.error(`${logPrefix}: Clerk.io error:`, response);
          resolve([]);
        }
      );
    });
  }

  getBaseQuery() {
    const queryObj = {
      key: this.dataset.ak,
      limit: RoComplementaryProducts.CLERK_LIMIT,
      attributes: [
        'id',
        'handle',
        'type',
        'categories_1',
        'gender',
        'color',
        'season',
      ],
    };

    if (this.dataset.productId) {
      queryObj.exclude = [this.dataset.productId];
    }

    return queryObj;
  }

  getSlotHandles(complementaryProducts, trendingProducts) {
    const handles = [];
    const usedProductIds = new Set();

    for (const slotCategories of Object.values(RoComplementaryProducts.SLOTS)) {
      const product =
        this.getProductForSlot(complementaryProducts, slotCategories, usedProductIds) ||
        this.getProductForSlot(trendingProducts, slotCategories, usedProductIds);

      if (!product) continue;

      usedProductIds.add(this.getProductId(product));
      handles.push(product.handle);
    }

    return handles;
  }

  getProductForSlot(products, slotCategories, usedProductIds) {
    const attributeTiers = [
      [true, true, true],
      [true, true, false],
      [true, false, true],
      [true, false, false],
      [false, true, true],
      [false, true, false],
      [false, false, true],
      [false, false, false],
    ];

    for (const [matchGender, matchColor, matchSeason] of attributeTiers) {
      const product = this.getProductForSlotWithAttributes(
        products,
        slotCategories,
        usedProductIds,
        matchGender,
        matchColor,
        matchSeason
      );

      if (product) {
        return product;
      }
    }

    return null;
  }

  getProductForSlotWithAttributes(products, slotCategories, usedProductIds, matchGender, matchColor, matchSeason) {
    for (const product of products) {
      if (!product.handle) continue;
      if (usedProductIds.has(this.getProductId(product))) continue;
      if (!this.productMatchesSlot(product, slotCategories)) continue;
      if (matchGender && !this.productMatchesAttribute(product, 'gender', this.dataset.productGender)) continue;
      if (matchColor && !this.productMatchesAttribute(product, 'color', this.dataset.productColor)) continue;
      if (matchSeason && !this.productMatchesAttribute(product, 'season', this.dataset.productSeason)) continue;

      return product;
    }

    return null;
  }

  productMatchesSlot(product, slotCategories) {
    const categories = this.getProductCategories(product);

    return categories.some((category) => {
      return slotCategories.includes(category);
    });
  }

  productMatchesAttribute(product, attributeName, expectedValue) {
    if (!expectedValue) return true;

    const value = product[attributeName];

    if (!value) return false;

    if (Array.isArray(value)) {
      return value.includes(expectedValue);
    }

    return String(value) === String(expectedValue);
  }

  getProductCategories(product) {
    const categories = product.categories_1;

    if (!categories) return [];

    return Array.isArray(categories) ? categories : [categories];
  }

  getProductId(product) {
    return product.id || product.handle;
  }
}

customElements.define('ro-complementary-products', RoComplementaryProducts);
